function clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function pointAt(points, t) {
  const first = points[0];
  if (first.time >= t) return { ...first, time: t };
  const last = points[points.length - 1];
  if (last.time <= t) return { ...last, time: t };
  const i = points.findIndex((p) => p.time > t);
  const p0 = points[i - 1];
  const p1 = points[i];
  const r = (t - p0.time) / (p1.time - p0.time);
  return {
    ...p0,
    time: t,
    x: p0.x + (p1.x - p0.x) * r,
    y: p0.y + (p1.y - p0.y) * r,
  };
}

/**
 * Split a scene at an absolute `time`, slicing trajectory and speaker_segments
 * so each half only carries the points that belong to it. Keeping the full
 * arrays on both halves makes the second half render the original path.
 */
export default function splitSceneAt(scene, time) {
  const t = time - (scene.start_time ?? 0);
  const a = { ...clone(scene), end_time: time };
  const b = { ...clone(scene), start_time: time };

  if (scene.trajectory?.length) {
    const cut = pointAt(scene.trajectory, t);
    a.trajectory = [...scene.trajectory.filter((p) => p.time < t), cut].map((p) => ({ ...p }));
    b.trajectory = [
      { ...cut, time: 0 },
      ...scene.trajectory.filter((p) => p.time > t).map((p) => ({ ...p, time: p.time - t })),
    ];
    // Load-time normalization parks crop at the first trajectory point; keep
    // that invariant so each half opens on its own path start.
    if (a.crop) a.crop = { ...a.crop, x: a.trajectory[0].x, y: a.trajectory[0].y };
    if (b.crop) b.crop = { ...b.crop, x: b.trajectory[0].x, y: b.trajectory[0].y };
  }

  // Segment times are SCENE-RELATIVE here, matching VerticalPreview.jsx:90-94.
  // render_from_json.py:292 documents them as absolute — the two disagree. Nothing
  // currently emits speaker_segments, so this is unreachable; whoever revives
  // bouncing has to reconcile the renderer with this convention.
  if (scene.speaker_segments?.length) {
    a.speaker_segments = scene.speaker_segments
      .filter((s) => s.start < t)
      .map((s) => ({ ...clone(s), end: Math.min(s.end, t) }));
    b.speaker_segments = scene.speaker_segments
      .filter((s) => s.end > t)
      .map((s) => ({ ...clone(s), start: Math.max(s.start, t) - t, end: s.end - t }));
  }

  return [a, b];
}
